/**
 * @krisspy-file
 * @type layout
 * @name "DashboardLayout"
 * @export "default"
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Archive,
  Calendar,
  Users,
  UserCheck,
  Settings,
  Menu,
  X,
  LogOut,
  MapPin,
  Tag,
  Mail,
  MailPlus,
  Ticket,
  RotateCcw,
  MessageSquare,
  XCircle,
  Wrench,
  BarChart3,
  ClipboardCheck,
  CalendarRange,
  ScanLine
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import MerchantSwitcher from '../components/MerchantSwitcher';
import { getMerchantById } from '../services/merchants.service';
import { ScanProvider, useBarcodeScanning } from '../contexts/ScanContext';
import GlobalScanHandler from '../components/scan/GlobalScanHandler';
import { isPathAllowedForRole } from '../lib/roleAccess';
import {
  DEFAULT_LOGO_ALT,
  DEFAULT_LOGO_URL,
  getWhitelabelBranding,
} from '../lib/whitelabel';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

interface MerchantSettings {
  cancellation_policy: any | null;
  sms: boolean | null;
  whatsapp: boolean | null;
  equipment_check_enabled: boolean | null;
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  // The scan provider sits here so every dashboard page (and its modals) can
  // receive scans; it installs nothing unless the merchant has scanning on.
  return (
    <ScanProvider>
      <DashboardLayoutContent>{children}</DashboardLayoutContent>
    </ScanProvider>
  );
}

function DashboardLayoutContent({ children }: DashboardLayoutProps) {
  const { t } = useTranslation();
  const { enabled: scanningEnabled } = useBarcodeScanning();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [merchantSettings, setMerchantSettings] = useState<MerchantSettings | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, signOut, roles, merchantId } = useAuth();
  // Role for nav gating is the ACTIVE merchant's role (not roles[0], which could
  // be a different merchant for a multi-merchant user).
  const currentRole = roles.find((r) => r.merchant_id === merchantId)?.role ?? roles[0]?.role;
  const { currentLanguage, availableAdminLanguages, changeLanguage, getLanguageConfig } = useLanguage();
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef<HTMLDivElement>(null);
  // On a whitelabel host the sidebar shows the partner's admin logo instead
  // of Click&Share; branding is static per domain, not per active merchant.
  const whitelabel = getWhitelabelBranding();
  const brandLogo = whitelabel
    ? whitelabel.logoUrl
      ? { url: whitelabel.logoUrl, alt: whitelabel.name }
      : null
    : { url: DEFAULT_LOGO_URL, alt: DEFAULT_LOGO_ALT };

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target as Node)) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update page title and load merchant settings for the ACTIVE merchant, so
  // switching merchant re-scopes the nav, title and conditional sections.
  useEffect(() => {
    const loadMerchantData = async () => {
      if (!merchantId) return;

      try {
        const { data: merchant, error } = await getMerchantById(merchantId);

        if (error || !merchant) return;

        // Update page title: capitalize first letter of merchant_id + " Admin Panel"
        const merchantName = merchant.merchant_id.charAt(0).toUpperCase() + merchant.merchant_id.slice(1);
        document.title = `${merchantName} Admin Panel`;

        // Store merchant settings for conditional navigation
        setMerchantSettings({
          cancellation_policy: merchant.cancellation_policy,
          sms: merchant.sms ?? null,
          whatsapp: merchant.whatsapp ?? null,
          equipment_check_enabled: (merchant as any).equipment_check_enabled ?? null
        });
      } catch (error) {
        console.error('Error loading merchant data:', error);
      }
    };

    loadMerchantData();
  }, [merchantId]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (profile?.full_name) {
      const names = profile.full_name.split(' ');
      return names.map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  // Build communication items based on merchant settings
  const communicationItems = [
    { name: t('navigation.confirmationEmails'), href: '/emails', icon: Mail }
  ];

  // Add conditional items based on merchant settings
  if (merchantSettings?.cancellation_policy) {
    communicationItems.push({ name: t('navigation.cancellationEmails'), href: '/cancellation-emails', icon: XCircle });
  }
  // Follow-up Emails is always available
  communicationItems.push({ name: t('navigation.followUpEmails'), href: '/follow-up-emails', icon: MailPlus });
  if (merchantSettings?.sms) {
    communicationItems.push({ name: t('navigation.smsMessages'), href: '/sms-messages', icon: MessageSquare });
  }
  if (merchantSettings?.whatsapp) {
    communicationItems.push({ name: t('navigation.whatsappMessages'), href: '/whatsapp-messages', icon: MessageSquare });
  }

  // Management section items — Equipment check is conditional on the merchant flag.
  const managementItems = [
    { name: t('navigation.bookings'), href: '/bookings', icon: Calendar },
    ...(scanningEnabled
      ? [{ name: t('navigation.quickBooking'), href: '/bookings/quick', icon: ScanLine }]
      : []),
    { name: t('navigation.customers'), href: '/customers', icon: UserCheck },
    { name: t('navigation.returnReports'), href: '/return-reports', icon: RotateCcw },
    ...(merchantSettings?.equipment_check_enabled
      ? [{ name: t('navigation.equipmentCheck'), href: '/equipment-check', icon: ClipboardCheck }]
      : []),
    { name: t('navigation.products'), href: '/products', icon: Package },
    { name: t('navigation.categories'), href: '/categories', icon: Tag },
    { name: t('navigation.discountCodes'), href: '/discount-codes', icon: Ticket },
    { name: t('navigation.inventory'), href: '/inventory', icon: Archive },
    { name: t('navigation.issues'), href: '/issues', icon: Wrench },
    { name: t('navigation.locations'), href: '/locations', icon: MapPin }
  ];

  const rawNavigation = [
    {
      section: t('navigation.overview'),
      items: [
        { name: t('navigation.dashboard'), href: '/dashboard', icon: LayoutDashboard },
        { name: t('navigation.analytics'), href: '/analytics', icon: BarChart3 },
        { name: t('navigation.availability'), href: '/availability', icon: CalendarRange }
      ]
    },
    {
      section: t('navigation.management'),
      items: managementItems
    },
    {
      section: t('navigation.communication'),
      items: communicationItems
    },
    {
      section: t('navigation.system'),
      items: [
        { name: t('navigation.settings'), href: '/settings', icon: Settings },
        { name: t('navigation.users'), href: '/users', icon: Users }
      ]
    }
  ];

  // Apply per-role path policy: drop items the current role can't access
  // and any sections that become empty as a result. For most roles this is
  // a no-op; for equipment_check it collapses the sidebar to the few
  // sections that role works in.
  const navigation = rawNavigation
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => isPathAllowedForRole(currentRole, item.href)),
    }))
    .filter((group) => group.items.length > 0);

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === path;
    }
    if (path === '/bookings' && location.pathname.startsWith('/bookings/quick')) {
      return false;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="h-screen bg-secondary flex overflow-hidden" data-testid="dashboard-layout">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 lg:left-0 bg-primary border-r border-primary z-30">
        {/* Logo */}
        <div className="h-16 flex items-center px-lg border-b border-primary">
          <div className="flex items-center">
            {brandLogo && (
              <img src={brandLogo.url} alt={brandLogo.alt} className="h-10 w-auto" />
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-lg px-md">
          {navigation.map((group) => (
            <div key={group.section} className="mb-lg">
              <h2 className="text-xs font-semibold text-secondary px-md mb-sm">
                {group.section}
              </h2>
              <div className="space-y-xs">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex items-center gap-md px-md py-sm rounded-lg transition-all duration-200 ${
                        active
                          ? 'bg-blue text-white shadow-sm'
                          : 'text-secondary hover:bg-secondary hover:text-primary'
                      }`}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      <span className="text-sm font-medium">{item.name}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* User Section */}
        <div className="p-md border-t border-primary">
          <div className="flex items-center gap-md p-md rounded-lg bg-secondary">
            <Link
              to="/profile"
              className="flex items-center gap-md flex-1 min-w-0 hover:opacity-80 transition-opacity"
              title={t('navigation.profile')}
            >
              <div className="w-8 h-8 rounded-full bg-blue flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                {getUserInitials()}
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium text-primary truncate">
                  {profile?.full_name || user?.email || 'User'}
                </p>
                <p className="text-xs text-secondary truncate">
                  {user?.email || 'No email'}
                </p>
              </div>
            </Link>
            <button
              onClick={handleSignOut}
              className="btn-icon"
              title={t('navigation.logout')}
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          data-testid="mobile-drawer-overlay"
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 w-64 flex flex-col bg-primary border-r border-primary z-50 lg:hidden transform transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-lg border-b border-primary">
          <div className="flex items-center">
            {brandLogo && (
              <img src={brandLogo.url} alt={brandLogo.alt} className="h-10 w-auto" />
            )}
          </div>
          <button
            data-testid="mobile-drawer-close-button"
            onClick={() => setSidebarOpen(false)}
            className="btn-icon"
            title="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation - scrollable area including user section */}
        <nav className="flex-1 overflow-y-auto py-lg px-md">
          {navigation.map((group) => (
            <div key={group.section} className="mb-lg">
              <h2 className="text-xs font-semibold text-secondary px-md mb-sm">
                {group.section}
              </h2>
              <div className="space-y-xs">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-md px-md py-sm rounded-lg transition-all duration-200 ${
                        active
                          ? 'bg-blue text-white shadow-sm'
                          : 'text-secondary hover:bg-secondary hover:text-primary'
                      }`}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      <span className="text-sm font-medium">{item.name}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Language Switcher - Mobile */}
          <div className="mt-lg pt-md border-t border-primary">
            <div className="flex flex-wrap gap-xs px-md">
              {availableAdminLanguages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`flex items-center gap-xs px-sm py-xs rounded-lg text-sm transition-colors ${
                    currentLanguage === lang.code
                      ? 'bg-blue text-white'
                      : 'text-secondary hover:bg-secondary hover:text-primary'
                  }`}
                >
                  <span>{lang.flag}</span>
                  <span>{lang.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* User Section - inside scrollable area */}
          <div className="mt-lg pt-md border-t border-primary">
            <div className="flex items-center gap-md p-md rounded-lg bg-secondary">
              <Link
                to="/profile"
                onClick={() => setSidebarOpen(false)}
                className="flex items-center gap-md flex-1 min-w-0 hover:opacity-80 transition-opacity"
                title={t('navigation.profile')}
              >
                <div className="w-8 h-8 rounded-full bg-blue flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                  {getUserInitials()}
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium text-primary truncate">
                    {profile?.full_name || user?.email || 'User'}
                  </p>
                  <p className="text-xs text-secondary truncate">
                    {user?.email || 'No email'}
                  </p>
                </div>
              </Link>
              <button
                onClick={handleSignOut}
                className="btn-icon"
                title={t('navigation.logout')}
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </nav>
      </aside>

      {/* Main Content Area */}
      <div className="dashboard-main-content">
        {/* Top Bar */}
        <header className="dashboard-topbar">
          <div className="dashboard-topbar-inner" data-testid="dashboard-topbar">
            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-drawer-open-button"
              onClick={() => setSidebarOpen(true)}
              className="mobile-menu-btn"
              title="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Right Actions */}
            <div className="topbar-actions" data-testid="dashboard-topbar-actions">
              {scanningEnabled && (
                <span
                  className="hidden sm:flex items-center gap-xs text-xs text-secondary"
                  title={t('scan.scannerReadyHint')}
                  data-testid="scanner-ready-indicator"
                >
                  <ScanLine className="w-4 h-4 text-success" />
                  {t('scan.scannerReady')}
                </span>
              )}
              {/* Merchant Switcher (only for multi-merchant users) */}
              <MerchantSwitcher />
              {/* Language Selector */}
              <div style={{ position: 'relative' }} ref={langDropdownRef}>
                <button
                  className="lang-selector"
                  onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                  title="Select language"
                >
                  <span className="text-sm font-medium">
                    {getLanguageConfig(currentLanguage).flag} {getLanguageConfig(currentLanguage).label}
                  </span>
                </button>
                {langDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: '4px',
                      minWidth: '160px',
                      zIndex: 50,
                    }}
                    className="bg-primary border border-primary rounded-lg shadow-lg overflow-hidden"
                  >
                    {availableAdminLanguages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          changeLanguage(lang.code);
                          setLangDropdownOpen(false);
                        }}
                        className={`w-full text-left px-md py-sm text-sm flex items-center gap-sm transition-colors ${
                          currentLanguage === lang.code
                            ? 'bg-blue text-white'
                            : 'text-primary hover:bg-secondary'
                        }`}
                      >
                        <span>{lang.flag}</span>
                        <span>{lang.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="dashboard-page-content">
          <div className="dashboard-page-inner">
            {children}
          </div>
        </main>
        {scanningEnabled && <GlobalScanHandler />}
      </div>
    </div>
  );
}
